import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  List,
  Modal,
  Paper,
  PasswordInput,
  Stack,
  Switch,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconAlertCircle, IconTrash } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import {
  CONSENT_SOURCE_LABELS,
  SUBSCRIPTION_PLAN_LABELS,
  type AccessState,
  type ConsentRecord,
  type ConsentState,
} from '@creativitas/shared';
import { toUserMessage } from '@/lib/api';
import { ConsentText } from '@/features/auth/ConsentCheckbox';
import { MY_CONSENTS_KEY, deleteMyAccount, fetchMyConsents, setMyConsent } from '@/features/privacy/api';

/**
 * Согласия, рассылки и удаление аккаунта в профиле (docs/PRIVACY.md).
 *
 * Рассылки человек включает и выключает сам — без письма в поддержку
 * (концепция v0.3, п. 5). Согласие на обработку данных так не снимается: без
 * него аккаунт существовать не может, и его отзыв — это удаление аккаунта.
 */

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });

function since(record: ConsentRecord | null, fallback: string): string {
  if (!record) return fallback;
  if (record.source === 'legacy') return `Дано при регистрации, ${formatDate(record.createdAt)}`;
  const verb = record.granted ? 'Дано' : 'Отозвано';
  const revision = record.revision ? `, редакция ${record.revision}` : '';
  return `${verb} ${formatDate(record.createdAt)} ${CONSENT_SOURCE_LABELS[record.source]}${revision}`;
}

export function ConsentsCard() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: MY_CONSENTS_KEY, queryFn: fetchMyConsents });

  const toggle = useMutation({
    mutationFn: ({ consentId, granted }: { consentId: string; granted: boolean }) => setMyConsent(consentId, granted),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(MY_CONSENTS_KEY, data);
      notifications.show({
        color: 'teal',
        message: variables.granted ? 'Рассылки включены' : 'Рассылки выключены — писем больше не будет',
      });
    },
    onError: (error) => notifications.show({ color: 'red', message: toUserMessage(error) }),
  });

  return (
    <Paper withBorder p="lg" radius="lg" style={{ borderColor: 'var(--border-subtle)' }}>
      <Stack gap="md">
        <div>
          <Title order={4}>Согласия и рассылки</Title>
          <Text size="sm" c="var(--text-muted)">
            Что вы разрешили платформе и когда. Рассылки можно выключить в любой момент.
          </Text>
        </div>

        {query.isLoading ? (
          <Text size="sm" c="var(--text-muted)">
            Загружаем...
          </Text>
        ) : query.isError || !query.data ? (
          <Alert color="red" icon={<IconAlertCircle size={17} />}>
            Не удалось загрузить согласия. {toUserMessage(query.error)}
          </Alert>
        ) : (
          <Stack gap="md">
            {query.data.consents
              .filter((consent) => consent.text !== null || consent.granted)
              .map((consent) => (
                <ConsentRow
                  key={consent.consentId}
                  consent={consent}
                  busy={toggle.isPending}
                  onToggle={(granted) => toggle.mutate({ consentId: consent.consentId, granted })}
                />
              ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

function ConsentRow({
  consent,
  busy,
  onToggle,
}: {
  consent: ConsentState;
  busy: boolean;
  onToggle: (granted: boolean) => void;
}) {
  const text = consent.text ?? consent.latest?.text ?? '';
  const links = consent.text !== null ? consent.links : (consent.latest?.links ?? []);

  if (consent.required) {
    return (
      <Stack gap={4} data-testid={`my-consent-${consent.consentId}`}>
        {/* Значок над текстом, а не рядом: на телефоне рядом он сжимал предложение в узкую колонку. */}
        <Badge size="sm" variant="light" color={consent.granted ? 'teal' : 'gray'} style={{ alignSelf: 'flex-start' }}>
          обязательное
        </Badge>
        <Text size="sm" style={{ lineHeight: 1.45 }}>
          <ConsentText consent={{ id: consent.consentId, text, links }} />
        </Text>
        <Text size="xs" c="var(--text-muted)">
          {since(consent.latest, 'Не записано')}. Отозвать его можно только вместе с удалением
          аккаунта — ниже.
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap={4} data-testid={`my-consent-${consent.consentId}`}>
      <Switch
        checked={consent.granted}
        disabled={busy}
        aria-label="Получать информационные рассылки"
        label={
          <Text size="sm" style={{ lineHeight: 1.45 }}>
            <ConsentText consent={{ id: consent.consentId, text, links }} />
          </Text>
        }
        onChange={(event) => onToggle(event.currentTarget.checked)}
      />
      <Text size="xs" c="var(--text-muted)" pl={54}>
        {since(consent.latest, 'Не включали')}
      </Text>
    </Stack>
  );
}

/**
 * Удаление аккаунта (политика, цель 1: «аккаунт удаляет сам Пользователь»).
 *
 * Сразу и безвозвратно — так обещает политика: «безвозвратное стирание с
 * серверов базы данных Платформы». Поэтому перед кнопкой — что исчезнет и что
 * останется, а сама кнопка просит пароль и отдельное «понимаю».
 */
export function DeleteAccountCard({ access, isAdmin }: { access: AccessState; isAdmin: boolean }) {
  const [opened, setOpened] = useState(false);
  const [understood, setUnderstood] = useState(false);
  const [password, setPassword] = useState('');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const remove = useMutation({
    mutationFn: () => deleteMyAccount({ password }),
    onSuccess: () => {
      /* Ничего от прежнего аккаунта не должно остаться и в памяти вкладки. */
      queryClient.clear();
      void navigate('/login', { replace: true, state: { accountDeleted: true } });
    },
  });

  const close = () => {
    setOpened(false);
    setUnderstood(false);
    setPassword('');
    remove.reset();
  };

  const plan = access.subscription.active && access.subscription.plan ? access.subscription : null;

  return (
    <Paper withBorder p="lg" radius="lg" style={{ borderColor: 'var(--border-subtle)' }}>
      <Stack gap="md">
        <div>
          <Title order={4}>Удаление аккаунта</Title>
          <Text size="sm" c="var(--text-muted)">
            Это же — отзыв согласия на обработку персональных данных.
          </Text>
        </div>
        <Group>
          <Button
            variant="outline"
            color="red"
            leftSection={<IconTrash size={16} />}
            onClick={() => setOpened(true)}
          >
            Удалить аккаунт…
          </Button>
        </Group>
      </Stack>

      <Modal opened={opened} onClose={close} title="Удалить аккаунт навсегда?" size="lg">
        <Stack gap="md">
          <Text size="sm">Сразу и без возможности восстановить будут удалены:</Text>
          <List size="sm" spacing={4}>
            <List.Item>аккаунт, имя и почта, вход на всех устройствах;</List.Item>
            <List.Item>доступ: тариф, купленные игры, выданные материалы;</List.Item>
            <List.Item>проведённые вами партии и МАК-сессии — вместе с именами участников;</List.Item>
            <List.Item>ваше имя в чужих партиях — там останется «Удалённый участник»;</List.Item>
            <List.Item>записи о ваших согласиях.</List.Item>
          </List>
          <Text size="sm" c="var(--text-muted)">
            Останутся только записи об оплатах — номер заказа, сумма и почта покупателя: их
            требуется хранить по закону.
          </Text>

          {plan ? (
            <Alert color="orange" icon={<IconAlertCircle size={17} />}>
              У вас действует тариф «{SUBSCRIPTION_PLAN_LABELS[plan.plan!]}»
              {plan.endsAt ? ` до ${formatDate(plan.endsAt)}` : ''} — он пропадёт вместе с аккаунтом,
              а деньги за оставшиеся дни автоматически не возвращаются.
            </Alert>
          ) : null}
          {access.ownedGames.length > 0 ? (
            <Alert color="orange" icon={<IconAlertCircle size={17} />}>
              Купленные игры ({access.ownedGames.map((game) => `«${game.title}»`).join(', ')}) будут
              недоступны: доступ привязан к аккаунту.
            </Alert>
          ) : null}
          {isAdmin ? (
            <Alert color="orange" icon={<IconAlertCircle size={17} />}>
              Вы администратор. Последнего администратора удалить нельзя — сначала назначьте другого.
            </Alert>
          ) : null}

          {remove.isError ? (
            <Alert color="red" icon={<IconAlertCircle size={17} />} title="Не удалось удалить">
              {toUserMessage(remove.error)}
            </Alert>
          ) : null}

          <Checkbox
            checked={understood}
            onChange={(event) => setUnderstood(event.currentTarget.checked)}
            label="Понимаю, что восстановить аккаунт будет нельзя"
          />
          <PasswordInput
            label="Пароль"
            description="Чтобы аккаунт не удалил тот, у кого случайно оказалась открыта ваша вкладка."
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={close}>
              Отмена
            </Button>
            <Button
              color="red"
              loading={remove.isPending}
              disabled={!understood || password === ''}
              onClick={() => remove.mutate()}
            >
              Удалить навсегда
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}
